import React, { useState, useCallback, useEffect } from "react";
import { Clock, Users, MapPin, Calendar, Info } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/Calendar/ui/hover-card";
import {
  type BlockOut,
  blockOutTypes,
  getBlockOutAccentColor,
  useScheduleData,
} from "@/components/Calendar/scheduleData";

interface BlockOutCardProps {
  blockOut: BlockOut;
  size?: "micro" | "small" | "medium" | "large";
  isDarkMode?: boolean;
  onEdit?: (blockOutId: string, blockOut: BlockOut) => void;
  onDurationChange?: (blockOutId: string, newEndTime: string) => void;
  enableResize?: boolean;
}

export function BlockOutCard({
  blockOut,
  size = "medium",
  isDarkMode = false,
  onEdit,
  onDurationChange,
  enableResize = false,
}: BlockOutCardProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const [tempEndTime, setTempEndTime] = useState(blockOut.endTime);

  const s = useScheduleData();
  const resources = (s as any)?.resources || [];
  const roomNameMap = (s as any)?.roomNameMap || {};

  // Clean, resolve and deduplicate affected staff names (exclude raw emails and IDs)
  const displayStaffNames = React.useMemo(() => {
    if (!blockOut.affectedPharmacists || blockOut.affectedPharmacists.length === 0) return [];
    const names = new Set<string>();
    blockOut.affectedPharmacists.forEach((p) => {
      const raw = String(p).trim();
      if (!raw) return;

      const foundResource = resources.find(
        (res: any) =>
          String(res.id || res._id).toLowerCase() === raw.toLowerCase() ||
          String(res.email || "").toLowerCase() === raw.toLowerCase() ||
          String(res.name || "").toLowerCase() === raw.toLowerCase()
      );

      if (foundResource && foundResource.name) {
        names.add(foundResource.name);
      } else if (!raw.includes("@") && !raw.match(/^[0-9a-fA-F]{24}$/)) {
        names.add(raw);
      }
    });
    return Array.from(names);
  }, [blockOut.affectedPharmacists, resources]);

  // Clean, resolve and deduplicate affected room names (exclude raw IDs and staff emails/names)
  const displayRoomNames = React.useMemo(() => {
    if (!blockOut.affectedRooms || blockOut.affectedRooms.length === 0) return [];
    const names = new Set<string>();
    blockOut.affectedRooms.forEach((r) => {
      const raw = String(r).trim();
      if (!raw) return;
      if (raw.includes("@")) return; // Skip emails

      // Skip staff resource names/emails if misclassified into rooms array
      const isStaffResource = resources.some(
        (res: any) =>
          (res.type === "Practitioner" || res.type === "Staff" || res.isPharmacist) &&
          (String(res.name || "").toLowerCase() === raw.toLowerCase() ||
            String(res.email || "").toLowerCase() === raw.toLowerCase())
      );
      if (isStaffResource) return;

      const resolvedName =
        roomNameMap[raw] ||
        roomNameMap[raw.toLowerCase()] ||
        Object.entries(roomNameMap).find(
          ([k]) => k.toLowerCase() === raw.toLowerCase()
        )?.[1];

      const foundResource = resources.find(
        (res: any) =>
          String(res.id || res._id).toLowerCase() === raw.toLowerCase() ||
          String(res.name || "").toLowerCase() === raw.toLowerCase()
      );

      if (resolvedName) {
        names.add(resolvedName);
      } else if (foundResource && foundResource.name) {
        names.add(foundResource.name);
      } else if (!raw.match(/^[0-9a-fA-F]{24}$/)) {
        names.add(raw);
      }
    });
    return Array.from(names);
  }, [blockOut.affectedRooms, resources, roomNameMap]);

  const handleBlockOutClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit && !isResizing) {
      onEdit(blockOut.id, blockOut);
    }
  };

  // Format time with different levels of detail
  const formatTime = (
    time: string,
    level: "minimal" | "compact" | "full" = "compact",
  ) => {
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "pm" : "am";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    if (level === "minimal") {
      // Just hour (e.g., "12" or "2")
      return minute === 0
        ? `${hour12}`
        : `${hour12}:${minute.toString().padStart(2, "0")}`;
    }

    if (level === "compact") {
      // Hour with period (e.g., "12pm" or "2:30pm")
      const timeStr =
        minute === 0
          ? `${hour12}`
          : `${hour12}:${minute.toString().padStart(2, "0")}`;
      return `${timeStr}${period}`;
    }

    // Full format (e.g., "12:00pm")
    return `${hour12}:${minute.toString().padStart(2, "0")}${period}`;
  };

  // Format time range
  const formatTimeRange = (
    start: string,
    end: string,
    level: "minimal" | "compact" | "full" = "compact",
  ) => {
    const startFormatted = formatTime(start, level);
    const endFormatted = formatTime(end, level);
    return `${startFormatted}-${endFormatted}`;
  };

  // Calculate duration in minutes from startTime and endTime
  const calculateDuration = (start: string, end: string): number => {
    const [startHour, startMin] = start.split(":").map(Number);
    const [endHour, endMin] = end.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes - startMinutes;
  };

  // Calculate end time from start time and duration
  const calculateEndTime = (
    startTime: string,
    durationMinutes: number,
  ): string => {
    const [startHour, startMin] = startTime.split(":").map(Number);
    const totalMinutes = startHour * 60 + startMin + durationMinutes;
    const endHour = Math.floor(totalMinutes / 60);
    const endMin = totalMinutes % 60;
    return `${endHour.toString().padStart(2, "0")}:${endMin.toString().padStart(2, "0")}`;
  };

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStartY(e.clientY);
    setTempEndTime(blockOut.endTime);
  };

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = e.clientY - resizeStartY;
      // Week view (micro): 56px per 30min = 1.867 px/min
      // Day view (large): 80px per 60min = 1.333 px/min
      // Week view (small): 64px per 60min = 1.067 px/min
      const pixelsPerMinute =
        size === "large" ? 80 / 60 : size === "micro" ? 56 / 30 : 64 / 60;
      const minutesChange = deltaY / pixelsPerMinute;

      // Round to nearest 5 minutes
      const roundedChange = Math.round(minutesChange / 5) * 5;
      const currentDuration = calculateDuration(
        blockOut.startTime,
        blockOut.endTime,
      );
      const newDuration = Math.max(15, currentDuration + roundedChange); // Minimum 15 minutes

      const newEndTime = calculateEndTime(blockOut.startTime, newDuration);
      setTempEndTime(newEndTime);
    },
    [isResizing, resizeStartY, blockOut.startTime, blockOut.endTime, size],
  );

  const handleResizeEnd = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      setIsResizing(false);

      if (tempEndTime !== blockOut.endTime && onDurationChange) {
        onDurationChange(blockOut.id, tempEndTime);
      }
    },
    [isResizing, tempEndTime, blockOut.endTime, blockOut.id, onDurationChange],
  );

  // Add/remove event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Calculate dynamic height for visual resize feedback
  const calculateHeight = (endTime: string) => {
    const duration = calculateDuration(blockOut.startTime, endTime);
    // Week view (micro): 56px per 30min = 112px per hour
    // Day view (large): 80px per hour
    // Week view (small): 64px per hour
    const pixelsPerHour = size === "large" ? 80 : size === "micro" ? 112 : 64;
    const hours = duration / 60;
    const calculatedHeight = hours * pixelsPerHour;
    const minHeight = size === "large" ? 60 : size === "micro" ? 30 : 40;
    return Math.max(minHeight, calculatedHeight);
  };

  // Hover card content component
  const HoverCardInfo = () => {
    const blockOutType = blockOutTypes.find((t) => t.value === blockOut.type);
    const duration = calculateDuration(blockOut.startTime, currentEndTime);

    return (
      <div
        className={`space-y-3 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
      >
        {/* Title */}
        <div className="flex items-start gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-full ${getBlockOutAccentColor(blockOut.type)} flex items-center justify-center text-white font-semibold text-lg`}
          >
            🚫
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">
              {blockOut.title}
            </div>
            <div
              className={`text-xs flex items-center gap-1 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <Clock className="w-3 h-3" />
              {formatTimeRange(blockOut.startTime, currentEndTime, "full")} (
              {duration} min)
            </div>
          </div>
        </div>

        {/* Block-Out Type */}
        <div className="flex items-center gap-2">
          <Calendar
            className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div
              className={`w-3 h-3 rounded-full ${getBlockOutAccentColor(blockOut.type)}`}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">
                {blockOutType?.label || blockOut.type}
              </div>
              <div
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {blockOutType?.description}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {blockOut.description && (
          <div className="flex items-start gap-2">
            <Info
              className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
            <p className="text-sm">{blockOut.description}</p>
          </div>
        )}

        {/* Affected Pharmacists */}
        {displayStaffNames && displayStaffNames.length > 0 && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Users
                className={`w-3 h-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
              <span
                className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Affected Staff:
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {displayStaffNames.map((pharmacist, idx) => (
                <span
                  key={idx}
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    isDarkMode
                      ? "bg-purple-900/30 text-purple-300"
                      : "bg-purple-100 text-purple-800"
                  }`}
                >
                  {pharmacist}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Affected Rooms */}
        {displayRoomNames && displayRoomNames.length > 0 && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-1.5 mb-1.5">
              <MapPin
                className={`w-3 h-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
              <span
                className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Affected Rooms:
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {displayRoomNames.map((room, idx) => (
                <span
                  key={idx}
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    isDarkMode
                      ? "bg-orange-900/30 text-orange-300"
                      : "bg-orange-100 text-orange-800"
                  }`}
                >
                  {room}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recurring Info */}
        {blockOut.isRecurring && blockOut.recurringPattern && (
          <div
            className={`pt-2 border-t border-gray-200 dark:border-gray-700 text-xs flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <Calendar className="w-3 h-3" />
            <span>Recurring: {blockOut.recurringPattern}</span>
          </div>
        )}
      </div>
    );
  };

  // Use temp end time during resize, otherwise use blockOut end time
  const currentEndTime = isResizing ? tempEndTime : blockOut.endTime;
  const currentDuration = calculateDuration(blockOut.startTime, currentEndTime);

  // Always calculate height when resize is enabled to support persistent visual spanning
  const dynamicHeight = enableResize
    ? calculateHeight(currentEndTime)
    : undefined;

  // Use absolute positioning for multi-slot block-outs (duration > 30 min) or during resize
  const isMultiSlot = currentDuration > 30;
  const shouldUseAbsolute = enableResize && (isResizing || isMultiSlot);

  // Micro size for week view with day columns
  if (size === "micro") {
    const calculateMicroHeight = (duration: number) => {
      // Week view: 56px per 30min = 112px per hour
      const pixelsPerHour = 112;
      const hours = duration / 60;
      const calculatedHeight = hours * pixelsPerHour;
      return Math.max(30, calculatedHeight); // Minimum 30px
    };

    const microHeight = enableResize
      ? calculateMicroHeight(currentDuration)
      : undefined;

    return (
      <HoverCard openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild>
          <div
            className={`group w-full ${shouldUseAbsolute ? "relative z-10" : "relative"} pointer-events-auto ${isResizing ? "transition-none" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`${shouldUseAbsolute ? "absolute top-0 left-0 right-0 shadow-lg" : "relative"} flex flex-col gap-0.5 pl-2 pr-2 py-1 rounded text-xs border backdrop-blur-sm ${isDarkMode ? "bg-gray-800/60 border-gray-700 text-gray-200" : "bg-white/60 border-gray-200 text-gray-900"} ${isResizing ? "cursor-ns-resize" : "cursor-pointer"} hover:shadow-md transition-shadow w-full overflow-hidden`}
              style={{
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 2px,
                  ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"} 2px,
                  ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"} 4px
                )`,
                ...(microHeight ? { height: `${microHeight}px` } : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isResizing) handleBlockOutClick(e);
              }}
              title=""
            >
              {/* Colored vertical bar for block-out type */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${getBlockOutAccentColor(blockOut.type)} rounded-l`}
              />
              {isResizing && enableResize && (
                <div className="absolute top-0.5 left-0.5 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded-full z-10">
                  {currentDuration}m
                </div>
              )}

              {/* Responsive content - shows more as space allows */}
              <div className="flex flex-col gap-0.5 min-h-0 flex-1">
                {/* Always show: Block title */}
                <div className="font-medium text-[11px] leading-tight truncate">
                  🚫 {blockOut.title}
                </div>

                {/* Show time when height > 35px */}
                {microHeight && microHeight > 35 && (
                  <div className="opacity-75 text-[10px] leading-tight">
                    {formatTime(blockOut.startTime, "minimal")}
                  </div>
                )}

                {/* Show block type when height > 50px */}
                {microHeight && microHeight > 50 && (
                  <div className="opacity-75 text-[9px] leading-tight truncate">
                    {
                      blockOutTypes.find((t) => t.value === blockOut.type)
                        ?.label
                    }
                  </div>
                )}
              </div>

              {/* Resize handle for micro cards */}
              {enableResize && (
                <div
                  className={`absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 ${
                    isResizing ? "opacity-100 bg-black/10" : ""
                  }`}
                  onMouseDown={handleResizeStart}
                >
                  <div
                    className={`w-6 h-0.5 rounded-full bg-white ${isResizing ? "opacity-100 shadow-lg" : "opacity-60"}`}
                  />
                </div>
              )}
            </div>
          </div>
        </HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="start"
          className={`w-80 z-[200] ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <HoverCardInfo />
        </HoverCardContent>
      </HoverCard>
    );
  }

  if (size === "large") {
    return (
      <HoverCard openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild>
          <div
            className={`group w-full ${shouldUseAbsolute ? "relative z-10" : "relative"} pointer-events-auto mb-2 ${isResizing ? "transition-none" : ""}`}
            onClick={handleBlockOutClick}
          >
            <div
              className={`${shouldUseAbsolute ? "absolute top-0 left-0 right-0 shadow-lg" : "relative"} pl-3 pr-3 py-3 rounded-lg border backdrop-blur-sm ${isDarkMode ? "bg-gray-800/60 border-gray-700 text-gray-200" : "bg-white/60 border-gray-200 text-gray-900"} ${isResizing ? "cursor-ns-resize" : "cursor-pointer"} hover:shadow-md transition-shadow overflow-hidden`}
              style={{
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 3px,
                  ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"} 3px,
                  ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"} 6px
                )`,
                ...(dynamicHeight ? { height: `${dynamicHeight}px` } : {}),
              }}
              title=""
            >
              {/* Colored vertical bar for block-out type */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 ${getBlockOutAccentColor(blockOut.type)} rounded-l`}
              />
              {isResizing && enableResize && (
                <div className="absolute top-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded-full z-10">
                  {currentDuration}m
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {formatTimeRange(
                      blockOut.startTime,
                      currentEndTime,
                      "compact",
                    )}
                  </div>
                  <div className="text-sm opacity-90 font-semibold truncate">
                    🚫 {blockOut.title}
                  </div>
                  <div className="text-sm opacity-75 truncate">
                    Type:{" "}
                    {
                      blockOutTypes.find((t) => t.value === blockOut.type)
                        ?.label
                    }
                  </div>
                  {blockOut.description && (
                    <div className="text-sm opacity-75 italic truncate">
                      {blockOut.description}
                    </div>
                  )}
                </div>
                <div className="text-sm font-medium opacity-75 flex-shrink-0 ml-2">
                  BLOCKED
                </div>
              </div>

              {/* Resize handle */}
              {enableResize && (
                <div
                  className={`absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 ${
                    isResizing ? "opacity-100 bg-black/10" : ""
                  }`}
                  onMouseDown={handleResizeStart}
                >
                  <div
                    className={`w-12 h-1 rounded-full bg-white ${isResizing ? "opacity-100 shadow-lg" : "opacity-60"}`}
                  />
                </div>
              )}
            </div>
          </div>
        </HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="start"
          className={`w-80 z-[200] ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <HoverCardInfo />
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div
          className={`group w-full ${shouldUseAbsolute ? "relative z-10" : "relative"} pointer-events-auto ${isResizing ? "transition-none" : ""}`}
          onClick={handleBlockOutClick}
        >
          <div
            className={`${shouldUseAbsolute ? "absolute top-0 left-0 right-0 shadow-lg" : "relative"} text-xs pl-2 pr-1 py-1 rounded border backdrop-blur-sm ${isDarkMode ? "bg-gray-800/60 border-gray-700 text-gray-200" : "bg-white/60 border-gray-200 text-gray-900"} ${isResizing ? "cursor-ns-resize" : "cursor-pointer"} hover:shadow-md transition-shadow overflow-hidden`}
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 2px,
                ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"} 2px,
                ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"} 4px
              )`,
              ...(dynamicHeight ? { height: `${dynamicHeight}px` } : {}),
            }}
            title=""
          >
            {/* Colored vertical bar for block-out type */}
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 ${getBlockOutAccentColor(blockOut.type)} rounded-l`}
            />
            {isResizing && enableResize && (
              <div className="absolute top-0.5 left-0.5 bg-black/80 text-white text-xs px-2 py-0.5 rounded-full z-10">
                {currentDuration}m
              </div>
            )}

            {/* Responsive content based on height */}
            <div className="flex flex-col gap-0.5 min-h-0">
              {/* Always show title */}
              <div className="font-medium text-[11px] leading-tight truncate text-center">
                🚫 {blockOut.title}
              </div>

              {/* Show time when height > 35px */}
              {size !== "small" && (!dynamicHeight || dynamicHeight > 35) && (
                <div className="text-[10px] opacity-75 leading-tight text-center">
                  {formatTime(blockOut.startTime, "minimal")}
                </div>
              )}

              {/* Show type when height > 50px */}
              {(!dynamicHeight || dynamicHeight > 50) && (
                <div className="text-[9px] opacity-75 leading-tight truncate text-center">
                  {blockOutTypes.find((t) => t.value === blockOut.type)?.label}
                </div>
              )}
            </div>

            {/* Resize handle for small/medium cards */}
            {enableResize && (
              <div
                className={`absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 ${
                  isResizing ? "opacity-100 bg-black/10" : ""
                }`}
                onMouseDown={handleResizeStart}
              >
                <div
                  className={`w-8 h-1 rounded-full bg-white ${isResizing ? "opacity-100 shadow-lg" : "opacity-60"}`}
                />
              </div>
            )}
          </div>
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        className={`w-80 z-[200] ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
      >
        <HoverCardInfo />
      </HoverCardContent>
    </HoverCard>
  );
}
